import type { OptimizationModel } from '@/shared/generated'

import {
	format,
	startOfMonth,
	startOfWeek,
	startOfYear,
	subDays,
	subMonths,
	subWeeks,
	subYears,
} from 'date-fns'
import { last, prop } from 'lodash/fp'

import { DateFormat, utcToZonedTime } from '@/shared/tools/dates'

/**
 * Month over month comparison: Current MTD vs. Previous MTD.
 */
export function getMoMPeriods(integrationDate: string) {
	const integration = utcToZonedTime(new Date(integrationDate), 'UTC')

	return {
		main: {
			start_date: format(startOfMonth(integration), DateFormat.system),
			end_date: format(integration, DateFormat.system),
		},
		compared: {
			start_date: format(
				startOfMonth(subMonths(integration, 1)),
				DateFormat.system
			),
			end_date: format(subMonths(integration, 1), DateFormat.system),
		},
	}
}

/**
 * Year over year comparison: Current YTD vs. Prior YTD
 */
export function getYoYPeriods(date: string) {
	const normalizedDate = utcToZonedTime(new Date(date), 'UTC')
	const priorDate = subYears(normalizedDate, 1)

	return {
		main: {
			start_date: format(startOfYear(normalizedDate), DateFormat.system),
			end_date: format(normalizedDate, DateFormat.system),
		},
		compared: {
			start_date: format(startOfYear(priorDate), DateFormat.system),
			end_date: format(priorDate, DateFormat.system),
		},
	}
}
/**
 * Last 90 days comparison: Current 90 days vs. Prior 90 days
 */
export function getLast90DaysPeriods() {
	const normalizedDate = utcToZonedTime(new Date(), 'UTC')

	return {
		main: {
			start_date: format(subDays(normalizedDate, 90), DateFormat.system),
			end_date: format(normalizedDate, DateFormat.system),
		},
		compared: {
			start_date: format(
				subDays(subYears(normalizedDate, 1), 90),
				DateFormat.system
			),
			end_date: format(
				subDays(subYears(normalizedDate, 1), 0),
				DateFormat.system
			),
		},
	}
}
/**
 * Returns a trailing 12-week period ending at the provided date.
 * The period starts from the Monday of the week 11 weeks prior.
 */
export function getT12WPeriod(date: string) {
	const end = new Date(date)
	const start = startOfWeek(subWeeks(end, 11), { weekStartsOn: 1 })

	return {
		start_date: format(start, DateFormat.system),
		end_date: format(end, DateFormat.system),
	}
}

export function getPercentOf(num: number, total: number): number {
	return (num / total) * 100
}

const getLastUpdateDateList = (list: OptimizationModel[]): Date[] =>
	list
		.map(prop('last_updated_date'))
		.filter(Boolean)
		.map((last_updated_date) => new Date(last_updated_date + 'Z'))
		.sort()
export const getLastUpdateDate = (list: OptimizationModel[]): Date =>
	last(getLastUpdateDateList(list)) as Date
